import React, { useState } from 'react';
import { X, Save, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Combobox } from './Combobox';
import { createMachine, MarcaOption } from '../api';

interface Props {
  onClose: () => void;
  marche: MarcaOption[];
  tipologie: { code: string; label: string }[];
  onCreated?: () => void;
}

/** Modal di creazione di una nuova macchina (marca + tipo + nome modello). */
export default function CreateMachineModal({ onClose, marche, tipologie, onCreated }: Props) {
  const [brand, setBrand] = useState<string | null>(null);
  const [tipo, setTipo] = useState<string | null>(null);
  const [nome, setNome] = useState('');
  const [saving, setSaving] = useState(false);

  const isValid = !!brand && nome.trim().length > 0;

  async function handleCreate() {
    if (!isValid || saving) return;
    setSaving(true);
    try {
      const brandLabel = marche.find(m => m.code === brand)?.label ?? brand!;
      await createMachine({ brand: brandLabel, tipo: tipo ?? undefined, nome: nome.trim() });
      toast.success('Macchina creata');
      onCreated?.();
      onClose();
    } catch (e: any) {
      toast.error('Errore: ' + (e?.response?.data?.detail ?? e?.message ?? 'unknown'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col" data-testid="create-machine-modal">
        <div className="flex-none px-6 py-4 border-b border-[#e4e4e1] flex items-center gap-3">
          <Plus className="w-4 h-4 text-[#2b3a8f] flex-none" />
          <h2 className="text-[17px] font-semibold text-[#1f1f1d] flex-1">Nuova macchina</h2>
          <button onClick={onClose} data-testid="create-machine-close" className="text-[#5c5c57] hover:text-[#3a3a36] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <Combobox
            label="Marca"
            value={brand}
            onChange={setBrand}
            options={marche}
            placeholder="Seleziona marca…"
            testId="create-machine-brand"
          />
          <Combobox
            label="Tipo macchina"
            value={tipo}
            onChange={setTipo}
            options={tipologie}
            placeholder="Seleziona tipologia… (opzionale)"
            testId="create-machine-tipo"
          />
          <div>
            <div className="text-[13px] font-semibold text-[#5c5c57] mb-1.5">Nome modello</div>
            <input
              value={nome}
              onChange={e => setNome(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }}
              data-testid="create-machine-nome"
              className="w-full px-2.5 py-[7px] border border-[#d8d8d4] rounded-[7px] text-[14px]
                focus:outline-none focus:border-[#2b3a8f] transition-colors"
              placeholder="Es. 1012"
            />
          </div>
        </div>

        <div className="flex-none px-6 py-4 border-t border-[#e4e4e1] flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            data-testid="create-machine-cancel"
            className="px-3.5 py-2 text-[14px] font-semibold text-[#5c5c57] hover:bg-[#fafafa] rounded-[7px] transition-colors"
          >
            Annulla
          </button>
          <button
            onClick={handleCreate}
            disabled={saving || !isValid}
            data-testid="create-machine-submit"
            className="px-3.5 py-2 rounded-[7px] bg-[#2b3a8f] text-white text-[14px] font-semibold
              hover:bg-[#243279] disabled:opacity-40 flex items-center gap-1.5 transition-colors"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Crea macchina
          </button>
        </div>
      </div>
    </div>
  );
}
